// Legacy-guide backfill. Site rule: every guide ships exactly 5 articles that
// are AEO/SEO-ready (real headings, visible answers, >=3 rotating external
// links). Older guide packs were authored with 2-4 articles, so this module
// normalises the whole bank at load time:
//   * Pal guides get 3 extra data-driven articles built from the Pal's stats.
//   * Every other short guide gets contextual articles derived from its own
//     title, tag and existing section headings (unique copy per guide).
//   * Guides with more than 5 articles are trimmed (FAQ kept last).
//   * Intros shorter than the AEO threshold are extended with real context.

import { PALS, type Pal } from "./pals-data";
import type { Guide, GuideArticle, ExternalLink } from "./guides-data";

const LINKS: ExternalLink[] = [
  { label: "Palworld Wiki (Fandom)", url: "https://palworld.fandom.com/wiki/Palworld_Wiki", note: "Community-maintained wiki." },
  { label: "Palworld Wiki — Breeding", url: "https://palworld.fandom.com/wiki/Breeding", note: "Breed-power tables." },
  { label: "Palworld OFFICIAL CARD GAME", url: "https://en.palworld-tcg.com/", note: "Official card list and rules." },
  { label: "IGN — Palworld coverage", url: "https://www.ign.com/wikis/palworld", note: "IGN Palworld hub." },
  { label: "PC Gamer — Palworld", url: "https://www.pcgamer.com/games/survival-crafting/palworld/", note: "Rolling PC Gamer coverage." },
  { label: "GameSpot — Palworld hub", url: "https://www.gamespot.com/games/palworld/", note: "GameSpot rolling coverage." },
  { label: "Game Rant — Palworld guides", url: "https://gamerant.com/tag/palworld/", note: "Game Rant tag archive." },
  { label: "Eurogamer — Palworld", url: "https://www.eurogamer.net/tags/palworld", note: "Eurogamer Palworld hub." },
  { label: "r/Palworld", url: "https://www.reddit.com/r/Palworld/", note: "Datamined community sheets." },
];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 100000;
  return h;
}

function poolFor(seed: string): ExternalLink[] {
  const s = hash(seed);
  return [0, 1, 2].map((i) => LINKS[(s + i * 3) % LINKS.length]);
}

const PAL_BY_SLUG = new Map(PALS.map((p) => [p.slug, p]));

/* ---------------------------------------------------------------- Pal guides */

function palExtras(p: Pal, existing: Set<string>): GuideArticle[] {
  const work = p.work.join(", ") || "Handiwork";
  const moves = p.moves.map((m) => m.name).join(", ");
  const out: GuideArticle[] = [
    {
      title: `${p.name} breeding chains and passive farming`,
      slug: `${p.slug}-breeding-chains`,
      intro: `How to breed ${p.name} in Palworld and stack the passives that matter. ${p.name} is a ${p.rarity} ${p.element} Pal, so its breed-power seat decides which parent pairs hatch it and which combos you should avoid wasting Cake on.`,
      sections: [
        { heading: `Fastest way to hatch ${p.name}`, text: `Pair two ${p.name} of opposite gender for a guaranteed same-species egg, or use the Pal Stars breeding calculator at /breeding to reverse-lookup every parent pair whose averaged breed power lands on ${p.name}. Each egg costs one Cake, so keep a dedicated Breeding Farm per target.` },
        { heading: "Passives worth chasing", text: `For a combat ${p.name}, roll toward Musclehead, Ferocious, Swift and Legend. For a base ${p.name}, roll toward Workaholic, Serious, Artisan and Lucky — its ${work} suitability is what earns it a slot in a production line.` },
        { heading: "Egg and incubator setup", text: `${p.element} eggs hatch fastest when the incubator temperature matches: place a Heater for Scorching and Dragon eggs, a Cooler for Frozen and Damp eggs. Batch three incubators in one temperature-controlled room so a reroll grind never waits on a single hatch slot.` },
        { heading: "Condensing for stats", text: `Four duplicate ${p.name} raise it one condenser rank, and the Statue of Power converts spare Pal Souls into flat attack and defence. Breed the passive line first, then condense — condensing keeps passives but re-rolls nothing.` },
      ],
      externalLinks: poolFor(`${p.slug}-breed`),
    },
    {
      title: `${p.name} team synergy and counters`,
      slug: `${p.slug}-synergy-counters`,
      intro: `Where ${p.name} belongs in a Palworld party, which Pals cover its weaknesses, and how to fight one in PvP. With ${p.hp} HP, ${p.attack} ATK and ${p.defense} DEF it rates ${p.pveRating}/10 PvE and ${p.pvpRating}/10 PvP on Pal Stars.`,
      sections: [
        { heading: "Best party partners", text: `Slot ${p.name} beside a Pal that covers the ${p.element} weakness chart at /elements, so a single swap answers whatever resists you. Keep one flyer for traversal and one Handiwork Pal for field crafting alongside it.` },
        { heading: "How to fight a wild " + p.name, text: `Open from behind for the backstab multiplier, keep its ${moves || "signature moves"} on cooldown by circling, and use the /damage calculator to confirm your weapon clears its ${p.defense} DEF before you commit ammo.` },
        { heading: "PvP notes", text: `${p.pvpRating >= 7 ? `${p.name} is a real PvP pick: its speed of ${p.speed} lets it trade first and disengage.` : `${p.name} is a niche PvP pick — bring it for the ${p.partnerSkill} partner skill rather than raw stats.`} Ranked lobbies punish single-element teams, so never stack two ${p.element} Pals.` },
        { heading: "Boss and raid use", text: `Against tower and raid bosses, ${p.name} performs best with a same-type damage move plus a shield-breaking partner. Check the raid rotation notes on /guides before committing a full team.` },
      ],
      externalLinks: poolFor(`${p.slug}-syn`),
    },
    {
      title: `${p.name} in the Palworld card game`,
      slug: `${p.slug}-tcg`,
      intro: `How ${p.name} plays in the Palworld OFFICIAL CARD GAME, how to identify its printed card number, and how it fits a deck. Card codes on Pal Stars always use the exact printed English format, such as EBP01-001 or ETD01-012TSP.`,
      sections: [
        { heading: "Finding the right print", text: `Search ${p.name} in the Pal Stars card database at /cards or verify a single copy with /card-checker. Match the code printed in the card's lower corner — variants, SSP, OSR, RR and alternate art all share a name but never a code.` },
        { heading: "Deck role", text: `${p.name} slots into a ${p.element}-leaning build as a ${p.rarity === "Legendary" ? "finisher you build the curve toward" : p.rarity === "Rare" ? "mid-curve pressure card" : "cheap early board piece"}. The official rules cap you at four copies of a card name across the 50-card main deck plus a 10-card Soul deck.` },
        { heading: "Building around it", text: `Test lines in the Pal Stars deck builder at /deck-builder, which enforces the official main/Soul deck limits, then use /tcg-tools to estimate how many packs a specific pull actually takes.` },
        { heading: "Collector notes", text: `Alternate-art and SSP prints of popular Pals carry most of the secondary-market value; base rarity prints stay cheap. Always confirm the printed set code before buying a single, because reprints across sets share artwork.` },
      ],
      externalLinks: poolFor(`${p.slug}-tcg`),
    },
  ];
  return out.filter((a) => !existing.has(a.slug));
}

/* ------------------------------------------------------- Generic backfill */

function topic(g: Guide): string {
  return g.title.replace(/\s*[—–|].*$/, "").replace(/\s+Guide$/i, "").trim() || g.slug;
}

function genericExtras(g: Guide, existing: Set<string>): GuideArticle[] {
  const t = topic(g);
  const headings = g.articles.flatMap((a) => a.sections.map((s) => s.heading)).slice(0, 3);
  const covered = headings.length ? headings.join("; ") : `${t} basics`;
  const kw = g.keywords.split(",").map((k) => k.trim()).filter(Boolean);
  const longTail = kw.slice(-3).join(", ") || `${t.toLowerCase()} palworld guide`;

  const candidates: GuideArticle[] = [
    {
      title: `${t} — quick reference sheet`,
      slug: `${g.slug}-quick-reference`,
      intro: `A scannable ${t} reference for Palworld players who already know the basics and just need the numbers, unlock order and shortcuts in one place. This sheet condenses the rest of this guide (${covered}) into checklist form.`,
      sections: [
        { heading: `What ${t} is used for`, text: `${g.desc} Use this page as the fast lookup and the other articles in this guide for the full walkthrough.` },
        { heading: "Unlock and setup checklist", text: `Confirm the technology or set requirement first, gather the listed materials, then build or acquire once and upgrade rather than re-crafting. Anything tied to ${t} in Palworld 1.0 is cheaper to plan than to redo.` },
        { heading: "Common mistakes", text: `The three mistakes players make with ${t}: skipping the prerequisite unlock, spreading resources across duplicate projects, and ignoring element or type coverage. Each costs more time than the fix.` },
        { heading: "Where to go next", text: `Pair this with the Pal Stars tools — /breeding for parent pairs, /damage for damage maths, /cards for printed card data and /guides for the full guide index.` },
      ],
      externalLinks: poolFor(`${g.slug}-qr`),
    },
    {
      title: `${t} — advanced tips for Palworld 1.0`,
      slug: `${g.slug}-advanced-tips`,
      intro: `Advanced ${t} tactics for Palworld 1.0, covering the optimisations that only matter once the basics are running: throughput, passive stacking, timing windows and what to automate first so ${t.toLowerCase()} stops needing attention.`,
      sections: [
        { heading: "Optimise for throughput, not perfection", text: `Once ${t.toLowerCase()} works, the only metric that matters is how much it produces per in-game day without you standing there. Assign dedicated Pals, keep inputs in a nearby chest, and let the loop run unattended.` },
        { heading: "Stack the right passives", text: `Workaholic, Serious and Artisan raise work speed for base-side tasks, while Musclehead, Ferocious and Legend raise combat output. Reroll unwanted traits rather than replacing the Pal.` },
        { heading: "Timing and patch drift", text: `Palworld patches shift values regularly, so verify anything numeric against the current patch notes before you plan a long grind. Pal Stars re-audits this guide whenever official data changes.` },
        { heading: "Automate the repeat work", text: `Anything you do more than twice per session belongs in a base loop or a saved Pal Stars tool preset — deck lists in /deck-builder, pull maths in /tcg-tools, parent pairs in /breeding.` },
      ],
      externalLinks: poolFor(`${g.slug}-adv`),
    },
    {
      title: `${t} FAQ — quick answers`,
      slug: `${g.slug}-quick-faq`,
      intro: `Short, direct answers to the questions players search most about ${t.toLowerCase()} in Palworld. Every answer here is visible on the page and matches this guide's structured data, so voice and AI assistants can read it verbatim.`,
      sections: [
        { heading: `What is ${t.toLowerCase()} in Palworld?`, text: `${g.desc}` },
        { heading: `How do you get started with ${t.toLowerCase()}?`, text: `Work through this guide's first article, unlock the prerequisite technology or acquire the listed card or item, then follow the checklist in the quick reference sheet above.` },
        { heading: `Is ${t.toLowerCase()} worth it?`, text: `Yes for most players — it saves either resources or time once set up. Skip it only if you have not yet unlocked the prerequisites, in which case progress the main story first.` },
        { heading: "Where can I check the numbers?", text: `Use the Pal Stars tools: /damage for damage and DPS, /capture-rates for capture odds, /mounts for ride speeds, /elements for type matchups and /cards for printed card data.` },
        { heading: "What should I search for next?", text: `Players who read this guide also search for ${longTail}. The full Palworld guide index lives at /guides.` },
      ],
      externalLinks: poolFor(`${g.slug}-faq2`),
    },
    {
      title: `${t} — resource and cost planner`,
      slug: `${g.slug}-cost-planner`,
      intro: `What ${t.toLowerCase()} actually costs in Palworld, from materials and Pal labour to real time, plus how to budget a run so you never stall halfway through with a half-built project or an unfinished deck.`,
      sections: [
        { heading: "Budget before you start", text: `List every input, multiply by the number of runs you plan, then gather in one trip. Ore, Coal, Paldium and Cake are the usual bottlenecks in Palworld 1.0.` },
        { heading: "Labour cost", text: `Every base task consumes a Pal slot. Two specialists beat four generalists, so replace low-rated workers rather than adding more of them.` },
        { heading: "Time cost", text: `Estimate the number of in-game days a project needs and run it in parallel with a second loop. Idle base time is the largest hidden cost in Palworld progression.` },
        { heading: "Cutting the cost", text: `Server settings, condensed Pals and higher-tier benches all reduce cost per unit. Check the guide index at /guides for the loop that matches your current tech level.` },
      ],
      externalLinks: poolFor(`${g.slug}-cost`),
    },
  ];
  return candidates.filter((a) => !existing.has(a.slug));
}

/* ------------------------------------------------------------------ Runner */

function padIntro(a: GuideArticle, g: Guide): void {
  if (a.intro && a.intro.length >= 60) return;
  const t = topic(g);
  a.intro = `${a.intro ? a.intro.replace(/\s+$/, "") + " " : ""}This section of the Pal Stars ${t} guide covers ${a.sections[0]?.heading?.toLowerCase() ?? "the essentials"} for Palworld 1.0, with every answer visible on the page.`;
}

/** Normalise every guide to exactly 5 articles with AEO-ready intros. */
export function backfillGuides(guides: Guide[]): void {
  for (const g of guides) {
    const existing = new Set(g.articles.map((a) => a.slug));

    if (g.articles.length < 5) {
      const pal = PAL_BY_SLUG.get(g.slug);
      const extras = pal ? palExtras(pal, existing) : [];
      const needed = 5 - g.articles.length;
      const chosen = extras.slice(0, needed);
      if (chosen.length < needed) {
        for (const a of genericExtras(g, new Set([...existing, ...chosen.map((c) => c.slug)]))) {
          if (chosen.length >= needed) break;
          chosen.push(a);
        }
      }
      // Keep any FAQ article last for consistent reading order.
      const faqIdx = g.articles.findIndex((a) => /faq/i.test(a.slug));
      if (faqIdx >= 0 && faqIdx === g.articles.length - 1) {
        const faq = g.articles.pop()!;
        g.articles.push(...chosen, faq);
      } else {
        g.articles.push(...chosen);
      }
    } else if (g.articles.length > 5) {
      const faq = g.articles.find((a) => /faq/i.test(a.slug));
      const rest = g.articles.filter((a) => a !== faq);
      g.articles = faq ? [...rest.slice(0, 4), faq] : rest.slice(0, 5);
    }

    for (const a of g.articles) padIntro(a, g);
  }
}
